import './resultsWithAvailabilities.scss';
import '../../TreatmentTypeDetails/treatmentTypeDatails.scss';

import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  IonAvatar,
  IonButton,
  IonIcon,
  IonItem,
  IonLabel,
  IonRadio,
  IonRadioGroup,
  IonSpinner,
} from '@ionic/react';
import injectables from 'application/pages/injectables';
import type { AvailableExpertsQuery } from 'application/pages/Treatments/ITreatmentAdapter';
import { TreatmentTypeDetails } from 'application/pages/Treatments/TreatmentResults';
import {
  TreatmentDetailsBeforeContent,
  TreatmentInfo,
} from 'application/pages/TreatmentTypeDetails';
import type ITreatmentTypeDetailsAdapter from 'application/pages/TreatmentTypeDetails/ITreatmentTypeDetailsAdapter';
import { useTreatmentBuilder } from 'application/state/TreatmentContext';
import type { ObjectID, TreatmentType } from 'application/types';
import { useInject } from 'inversify-hooks';
import { star, starOutline } from 'ionicons/icons';
import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import Rating from 'react-rating';
import CustomModal from 'ui/elements/CustomModal/CustomModal';
import SubPageWrapperForModal from 'ui/elements/CustomModal/SubPageWrapperForModal';
import { useContextTranslation, useTranslateString } from 'ui/translation';

import useCalcMaxHeight from '../../../../ui/utils/useCalcMaxHeight';

interface ResultsWithAvailabilitiesCard {
  result: AvailableExpertsQuery;
  price: number | null;
}

export const TreatmentTypeDetailsModal = ({
  treatmentTypeId,
  setTreatmentTypeDetails,
}: {
  treatmentTypeId: string | null;
  setTreatmentTypeDetails: (treatmentTypeId: ObjectID | null) => void;
}): JSX.Element => {
  const [adapter] = useInject<ITreatmentTypeDetailsAdapter>(
    injectables.TreatmentDetailsAdapter,
  );

  const { locationId } = useTreatmentBuilder();

  const treatment = adapter.useTreatmentDetails(locationId);
  const [
    treatmentElement,
    setTreatmentElement,
  ] = useState<TreatmentType | null>(null);

  useEffect(() => {
    if (treatment.value) {
      setTreatmentElement(
        treatment.value.find(el => treatmentTypeId === el.id) || null,
      );
    }
  }, [treatment, treatmentTypeId, setTreatmentElement]);

  return (
    <>
      <CustomModal
        isOpen={Boolean(treatmentTypeId)}
        onDidDismiss={e => {
          e.stopPropagation();
          setTreatmentTypeDetails(null);
        }}
      >
        <SubPageWrapperForModal
          wrapperClassName="expert-profile"
          beforeContent={
            <div className="treatment-details-before-content">
              {treatmentElement && (
                <TreatmentDetailsBeforeContent treatment={treatmentElement} />
              )}
            </div>
          }
          onClose={e => {
            e.stopPropagation();
            setTreatmentTypeDetails(null);
          }}
        >
          {!treatmentElement && <IonSpinner />}
          {treatmentElement && <TreatmentInfo treatment={treatmentElement} />}
        </SubPageWrapperForModal>
      </CustomModal>
    </>
  );
};

const TreatmentTypeItem: React.FC<{
  itemType: TreatmentType;
  itemExpert: AvailableExpertsQuery;
  setTreatmentTypeDetails: (treatmentTypeId: ObjectID | null) => void;
}> = ({ itemType, itemExpert, setTreatmentTypeDetails }) => {
  const translate = useTranslateString();
  const {
    experts,
    expertId,
    setType,
    setExpertId,
    setExperts,
    isFirstExpertSelection,
  } = useTreatmentBuilder();
  const t = useContextTranslation('page.treatment');

  return (
    <IonItem key={`${itemType.id}_${expertId}`} lines="none" mode="md">
      <IonButton
        className="treatment-details-icon"
        color="white"
        onClick={e => {
          e.stopPropagation();
          setTreatmentTypeDetails(itemType.id);
        }}
      >
        i
      </IonButton>
      <IonLabel
        onClick={e => {
          e.stopPropagation();
          setTreatmentTypeDetails(itemType.id);
        }}
        className="type-label"
        mode="ios"
        color="primary"
      >
        {translate(itemType.name)}
      </IonLabel>
      <IonButton
        fill="clear"
        type="button"
        className="select-wrapper"
        onClick={e => {
          setExperts(
            !isFirstExpertSelection && experts?.[0]
              ? [
                  experts?.[0],
                  {
                    expertId: itemExpert.id,
                    name: itemExpert.name,
                    profilePictureUrl: itemExpert.profilePictureUrl,
                    type: itemType,
                  },
                ]
              : [
                  {
                    expertId: itemExpert.id,
                    name: itemExpert.name,
                    profilePictureUrl: itemExpert.profilePictureUrl,
                    type: itemType,
                  },
                ],
          );
          e.stopPropagation();
          setExpertId(itemExpert.id);
          setType(itemType);
        }}
        mode="md"
      >
        <IonLabel mode="md" className="select-label">
          {t('select')}
        </IonLabel>
        <IonRadio mode="md" slot="end" value={itemType.id} />
      </IonButton>
    </IonItem>
  );
};

const TreatmentTypesList = ({ expert }: { expert: AvailableExpertsQuery }) => {
  const t = useContextTranslation('page.treatment');

  const [expanded, setExpanded] = React.useState<boolean>(false);
  const [ref, maxHeight] = useCalcMaxHeight(expanded);

  const { setTreatmentTypeDetailsId } = useContext(TreatmentTypeDetails);

  const { type, expertId: builderExpertId, locationId } = useTreatmentBuilder();
  const [adapter] = useInject<ITreatmentTypeDetailsAdapter>(
    injectables.TreatmentDetailsAdapter,
  );

  const availableTypes = adapter.useTreatmentDetails(locationId || null);

  const availableExpertTypes = availableTypes.value?.filter(treatmentType =>
    expert.treatmentTypes.includes(treatmentType.id),
  );

  const hiddenTypes = availableExpertTypes?.slice(3) || [];
  const showedTypes = availableExpertTypes?.slice(0, 3) || [];

  useEffect(() => {
    if (expert.id !== builderExpertId || expanded) {
      return;
    }
    if (hiddenTypes?.find(el => el.id === type?.id)) {
      setExpanded(true);
    }
  }, [expanded, type?.id]);

  if (availableTypes.loading) {
    return <IonSpinner name="dots" />;
  }

  if (!availableExpertTypes?.length) {
    return <></>;
  }

  return (
    <div className="type-list-wrapper">
      <IonRadioGroup
        allowEmptySelection
        value={expert.id === builderExpertId ? type?.id : null}
      >
        {showedTypes.map((treatmentType: TreatmentType) => (
          <TreatmentTypeItem
            key={`${treatmentType.id}_${expert.id}`}
            itemType={treatmentType}
            itemExpert={expert}
            setTreatmentTypeDetails={setTreatmentTypeDetailsId}
          />
        ))}
        <div className="expandable-select">
          <div
            className={`selectable-container  ${
              !maxHeight ? 'selectable-hidden__' : ''
            }`}
            ref={ref}
          >
            {hiddenTypes.map((treatmentType: TreatmentType) => (
              <TreatmentTypeItem
                key={`${treatmentType.id}_${expert.id}`}
                itemType={treatmentType}
                itemExpert={expert}
                setTreatmentTypeDetails={setTreatmentTypeDetailsId}
              />
            ))}
          </div>
        </div>
        <IonButton
          className={`show-more-btn${
            expanded || !hiddenTypes.length ? ' hidden' : ''
          }`}
          expand="block"
          onClick={() => {
            setExpanded(true);
          }}
          fill="clear"
        >
          {t('show_more')}
          <FontAwesomeIcon icon={faChevronDown} />
        </IonButton>
      </IonRadioGroup>
    </div>
  );
};

const ResultsWithAvailabilities: React.FC<ResultsWithAvailabilitiesCard> = ({
  result,
  price,
}) => {
  const t = useContextTranslation('page.treatment');
  const { setExpertId } = useContext(TreatmentTypeDetails);
  const { location, date } = useTreatmentBuilder();

  return (
    <div className={`treatment-with-result-card${!date ? ' inactive' : ''}`}>
      <div className="card-header">
        <IonAvatar
          onClick={e => {
            e.stopPropagation();
            setExpertId(result.id);
          }}
        >
          <img src={result.profilePictureUrl} alt={t('profile_picture')} />
        </IonAvatar>
        <div
          className={`expert-info-container${
            location && date ? ' fulfilled' : ''
          }`}
        >
          <div className="name">{result.name}</div>
          <div className="rating">
            {(result.rating / 10).toFixed(1)}
            <Rating
              start={0}
              stop={5}
              initialRating={Math.round(result.rating / 10)}
              emptySymbol={<IonIcon icon={starOutline} />}
              fullSymbol={<IonIcon icon={star} />}
              readonly
              className="rating-stars"
            />
          </div>
          {Boolean(date) && (
            <div className="profile">
              <button
                type="button"
                className="profile-btn"
                onClick={e => {
                  e.stopPropagation();
                  setExpertId(result.id);
                }}
              >
                {t('view_profile')}
              </button>
            </div>
          )}
        </div>
        {price && (
          <div className="price">
            <Trans t={t} i18nKey="price_from" values={{ price }}>
              From
              <span className="price-label">
                {{ price }} € <span className="hour-label">p.hr</span>
              </span>
            </Trans>
          </div>
        )}
      </div>
      {location?.postalCode && date && (
        <div className="treatment-types">
          <TreatmentTypesList expert={result} />
        </div>
      )}
    </div>
  );
};

export default ResultsWithAvailabilities;
